import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Form, Formik } from 'formik';

import { Input } from '@/common';
import { ErrorPage } from '@/components/common/ErrorPage';
import {
  Breadcrumb,
  Button,
  Label,
  LABEL_TYPE,
  Spinner,
  Textarea,
  ToggleSwitch
} from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { queryClient } from '@/configuration/react-query';
import { PRIVATE_PAGE_ROUTES } from '@/enums';
import { isDigitalSignageSelected, isMediaLibraryPermission, updateOrganizationPermissionIds } from '@/helpers';
import { showToast, ToastType } from '@/helpers/toast';
import { createUpdatePaymentPlanValidation } from '@/pages/PaymentPlans/PaymentPlanValidation';
import { PaymentPlanIcon } from '@/resources';
import { getAllOrganizationPermissions } from '@/services/organizationPermissions.service';
import {
  useCreatePaymentPlanMutation,
  useGetPaymentPlanById,
  useUpdatePaymentPlanMutation
} from '@/services/paymentPlan.service';
import { CreateEditPaymentPlanPayload, OrganizationPermission } from '@/types';

export const CreateEditPaymentPlan = () => {
  const { planId } = useParams();
  const [t] = useTranslation('common');
  const isEditPage = !!planId;
  const [error, setError] = useState<number>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const navigate = useNavigate();

  const {
    data: paymentPlanData,
    isLoading,
    isRefetching
  } = useGetPaymentPlanById(Number(planId), {
    onSuccess: () => undefined,
    onError: (error) => {
      setError(error.status);
    }
  });

  const { mutate: createPaymentPlan } = useCreatePaymentPlanMutation(queryClient, {
    onSuccess: () => {
      navigate(PRIVATE_PAGE_ROUTES.PAYMENT_PLANS);
      showToast(ToastType.SUCCESS, t('paymentPlanSuccesfullyCreated'));
      setIsSubmitting(false);
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
      setIsSubmitting(false);
    }
  });

  const { mutate: updatePaymentPlan } = useUpdatePaymentPlanMutation(queryClient, {
    onSuccess: () => {
      navigate(PRIVATE_PAGE_ROUTES.PAYMENT_PLANS);
      showToast(ToastType.SUCCESS, t('paymentPlanSuccesfullyUpdated'));
      setIsSubmitting(false);
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
      setIsSubmitting(false);
    }
  });

  const { data: response } = getAllOrganizationPermissions(undefined, 'Id', true);

  const organizationPermissions = (response?.data?.items as OrganizationPermission[]) || [];

  const middleIndex = Math.ceil(organizationPermissions.length / 2);
  const firstHalf = organizationPermissions.slice(0, middleIndex);
  const secondHalf = organizationPermissions.slice(middleIndex);

  const getInitialValues = () => {
    const initialValues = {
      id: paymentPlanData?.data.id,
      name: paymentPlanData?.data.name || '',
      description: paymentPlanData?.data.description || '',
      organizationPermissionIds: paymentPlanData?.data.organizationPermissions
        ? paymentPlanData.data.organizationPermissions.map((op: OrganizationPermission) => op.id)
        : []
    };
    return initialValues;
  };

  const handleSubmit = (values: CreateEditPaymentPlanPayload) => {
    setIsSubmitting(true);
    const payload = {
      ...values
    };

    if (isEditPage) {
      updatePaymentPlan(payload);
    } else {
      createPaymentPlan(payload);
    }
  };

  return (
    <div className='flex size-full flex-col px-3'>
      {(isEditPage && isLoading) || isRefetching ? (
        <Spinner />
      ) : error ? (
        <ErrorPage code={error} />
      ) : (
        <div className='!md:w-full md:mx-20 md:mt-7'>
          <Breadcrumb
            icon={<PaymentPlanIcon className='stroke-[0.5] text-lg text-primary-blue' />}
            iconPath={PRIVATE_PAGE_ROUTES.PAYMENT_PLANS}
            path={isEditPage ? [{ label: t('editPlan') }] : [{ label: t('addPlan') }]}
          />
          {isEditPage && (
            <div className='mt-6 hidden gap-x-6 md:flex'>
              <Label text={t('editPlan')} type={LABEL_TYPE.H1} />
            </div>
          )}
          <div className='mt-4 flex w-full flex-col justify-center'>
            <Formik
              enableReinitialize
              initialValues={getInitialValues()}
              onSubmit={handleSubmit}
              validationSchema={createUpdatePaymentPlanValidation}>
              {({ isValid, dirty, setFieldValue, values, errors, touched }) => (
                <Form id='createEditPaymentPlan'>
                  <div className='flex w-full flex-col'>
                    <div className='mt-2 md:mt-5'>
                      <Input
                        id='name'
                        label={t('paymentPlanName')}
                        type='text'
                        isRequired
                        width={'w-full'}
                        className='rounded-lg'
                        errorMessage={
                          errors.name && touched.name ? errors.name.toString() : undefined
                        }
                        isInvalid={!!(errors.name && touched.name)}
                      />
                      <div className='mt-2 md:mt-5'>
                        <Textarea
                          label={t('description')}
                          id='description'
                          className='h-24 w-full'
                        />
                      </div>
                      <div className='mt-2 md:mt-5'>
                        <Label text={t('services')} type={LABEL_TYPE.PARAGRAPH2} />
                        <div className='flex w-full flex-col md:flex-row'>
                          <div className='flex flex-col md:w-full'>
                            {firstHalf.map((permission: OrganizationPermission) => (
                              <div
                                key={permission.id}
                                className='mt-6 flex w-full flex-row justify-between md:pr-8'>
                                <Label
                                  text={t(`${permission.name}`)}
                                  type={LABEL_TYPE.PARAGRAPH2}
                                />
                                <ToggleSwitch
                                  handleToggle={() => {
                                    const sortedPermissionsIds = updateOrganizationPermissionIds(
                                      organizationPermissions,
                                      values.organizationPermissionIds ?? [],
                                      permission
                                    );

                                    setFieldValue('organizationPermissionIds', sortedPermissionsIds);
                                  }}
                                  toggled={values.organizationPermissionIds?.includes(permission.id) as boolean}
                                  disabled={
                                    isMediaLibraryPermission(permission) &&
                                    isDigitalSignageSelected(
                                      organizationPermissions,
                                      values.organizationPermissionIds
                                    )
                                  }
                                />
                              </div>
                            ))}
                          </div>
                          <div className='mt-6 hidden w-[3px] bg-secondary-blue-gray md:flex'></div>
                          <div className='flex flex-col md:w-full'>
                            {secondHalf.map((permission: OrganizationPermission) => (
                              <div
                                key={permission.id}
                                className='mt-6 flex w-full flex-row justify-between md:pl-8'>
                                <Label
                                  text={t(`${permission.name}`)}
                                  type={LABEL_TYPE.PARAGRAPH2}
                                />
                               <ToggleSwitch
                                  handleToggle={() => {
                                    const sortedPermissionsIds = updateOrganizationPermissionIds(
                                      organizationPermissions,
                                      values.organizationPermissionIds ?? [],
                                      permission
                                    );

                                    setFieldValue('organizationPermissionIds', sortedPermissionsIds);
                                  }}
                                  toggled={values.organizationPermissionIds?.includes(permission.id) as boolean}
                                  disabled={
                                    isMediaLibraryPermission(permission) &&
                                    isDigitalSignageSelected(
                                      organizationPermissions,
                                      values.organizationPermissionIds
                                    )
                                  }
                               />
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className='my-5 flex w-full justify-end md:mb-6 md:mt-10'>
                        <Button
                          type='submit'
                          className='w-full md:w-auto'
                          variant={ButtonVariant.PRIMARY}
                          disabled={isSubmitting || !(isValid && dirty)}
                          handleOnClick={() => handleSubmit}>
                          {isEditPage ? t('saveChanges') : t('addPlan')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      )}
    </div>
  );
};
